import { useMemo, useRef } from 'react';

import { useQueries } from '@tanstack/react-query';

import {
  getApiV5FirmsRequestsRequestIdSectionsSectionIdItems,
  RequestItemsSchema,
  useGetApiV5FirmsRequestsRequestIdSections,
} from '@liscio/api';
import { useRequestStore } from '@liscio/common';

export const useGetAllItemsInSections = (enabled = true) => {
  const cachedItemsRef = useRef<RequestItemsSchema[]>([]);
  const { request, requestId } = useRequestStore();

  const { data: sectionsData } = useGetApiV5FirmsRequestsRequestIdSections(
    requestId!,
    {
      per_page: 100,
    },
    {
      query: { enabled: request?.sectionsIds?.length === 0 && enabled },
    }
  );

  const sectionUUIDs = request?.sectionsIds?.length
    ? request?.sectionsIds
    : sectionsData?.data?.map((section) => section.uuid);

  const queryResults = useQueries({
    queries: sectionUUIDs
      ? sectionUUIDs.map((sectionUUID) => ({
          queryKey: ['items', 'section', sectionUUID],
          queryFn: () =>
            getApiV5FirmsRequestsRequestIdSectionsSectionIdItems(
              requestId!,
              sectionUUID
            ),
          enabled: Boolean(sectionUUID && requestId) && enabled,
        }))
      : [],
  });

  // Process results
  const isLoading = useMemo(
    () => queryResults.some((result) => result.isLoading),
    [queryResults]
  );
  const isError = useMemo(
    () => queryResults.some((result) => result.isError),
    [queryResults]
  );
  const error = useMemo(
    () => queryResults.find((result) => result.error)?.error,
    [queryResults]
  );

  // Filter successful query results once
  const successfulResults = queryResults.filter(
    (result) => result.data && !result.isLoading && !result.isError
  );

  // Generate both arrays from the filtered results
  const items = successfulResults.flatMap((result) => result.data?.data || []);

  // Check if items are already cached
  if (items.length) {
    cachedItemsRef.current = items;
  }

  // Pair each items query with its section UUID. Do not index into
  // `request.sectionsIds` via `successfulResults` — that misaligns when some
  // section queries fail or finish out of order.
  const itemsWithSections = useMemo(() => {
    if (!sectionUUIDs?.length) return [];
    return sectionUUIDs.map((sectionId, index) => ({
      sectionId,
      items: queryResults[index]?.data?.data ?? [],
    }));
  }, [sectionUUIDs, queryResults]);

  return useMemo(
    () => ({
      items: cachedItemsRef.current,
      itemsWithSections,
      isLoading,
      isError,
      error,
    }),
    [itemsWithSections, isLoading, isError, error]
  );
};
